import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { API_BASE, getImageUrl } from '../../utils/api';
import { toast } from 'react-toastify';
import { generateDeveloperSampleExcel, parseDeveloperExcel } from '../../utils/excelTemplate';
import ConfirmModal from '../../components/modals/ConfirmModal';

interface DeveloperUser {
    _id: string;
    name: string;
    email: string;
    avatar: string;
    phone: string;
    projects: string;
    expertise: string;
    city: string;
    about: string;
    totalProjects: string;
    projectsCompleted: string;
    projectsOngoing: string;
    projectsUpcoming: string;
    projectsSince: string;
    deliveryRecord: string;
    isActive: boolean;
    isFeatured: boolean;
    createdAt: string;
}

export default function AdminDevelopers() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [developers, setDevelopers] = useState<DeveloperUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [importing, setImporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [confirmModal, setConfirmModal] = useState({ 
        isOpen: false, 
        title: '', 
        message: '', 
        onConfirm: () => {} 
    });
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const fetchDevelopers = useCallback(() => {
        setLoading(true);
        fetch(`${API_BASE}/developers`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) setDevelopers(data);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => { 
        fetchDevelopers(); 
    }, [fetchDevelopers]);

    const toggleFeatured = async (dev: DeveloperUser) => {
        try {
            const res = await fetch(`${API_BASE}/developers/${dev._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ isFeatured: !dev.isFeatured })
            });
            if (res.ok) {
                setDevelopers(developers.map(d => d._id === dev._id ? { ...d, isFeatured: !d.isFeatured } : d));
                toast.success(`Developer ${!dev.isFeatured ? 'featured' : 'unfeatured'} successfully`);
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to update featured status');
        }
    };

    const toggleStatus = async (dev: DeveloperUser) => {
        try {
            const res = await fetch(`${API_BASE}/developers/${dev._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ isActive: !dev.isActive })
            });
            if (res.ok) {
                setDevelopers(developers.map(d => d._id === dev._id ? { ...d, isActive: !d.isActive } : d));
                toast.success(`Developer account ${!dev.isActive ? 'activated' : 'locked'} successfully`);
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to update status');
        }
    };

    const deleteDev = (id: string) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Developer',
            message: 'Are you sure you want to delete this developer? This action cannot be undone.',
            onConfirm: async () => {
                try {
                    const res = await fetch(`${API_BASE}/developers/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        setMsg('Developer deleted successfully');
                        setDevelopers(prev => prev.filter(d => d._id !== id));
                        setSelectedIds(prev => prev.filter(sid => sid !== id));
                        setTimeout(() => setMsg(''), 3000);
                    }
                } catch (err) {
                    console.error(err);
                }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const deleteSelected = () => {
        if (selectedIds.length === 0) return;
        setConfirmModal({
            isOpen: true,
            title: 'Delete Multiple Developers',
            message: `Are you sure you want to delete ${selectedIds.length} selected developers? This action cannot be undone.`,
            onConfirm: async () => {
                try {
                    const res = await fetch(`${API_BASE}/developers/bulk/delete`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ ids: selectedIds })
                    });

                    if (res.ok) {
                        const result = await res.json();
                        setMsg(result.message || 'Developers deleted successfully');
                        setDevelopers(prev => prev.filter(d => !selectedIds.includes(d._id)));
                        setSelectedIds([]);
                        setTimeout(() => setMsg(''), 3000);
                    } else {
                        const err = await res.json();
                        toast.error(err.message || 'Failed to delete developers');
                    }
                } catch (err) {
                    console.error(err);
                    toast.error('An error occurred while deleting');
                }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredDevelopers.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredDevelopers.map(d => d._id));
        }
    };

    const toggleSelectOne = (id: string) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(sid => sid !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const filteredDevelopers = developers.filter(d => 
        d.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        d.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setImporting(true);
            const data = await parseDeveloperExcel(file);
            console.log("Parsed Excel Data:", data);
            if (data.length === 0) {
                toast.error("Excel file is empty or invalid format.");
                setImporting(false);
                return;
            }

            const res = await fetch(`${API_BASE}/developers/bulk-import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ developers: data })
            });

            const result = await res.json();
            if (res.ok) {
                toast.success(result.message || "Developers imported successfully!");
                fetchDevelopers();
            } else {
                toast.error(result.message || "Failed to import developers");
            }
        } catch (err) {
            console.error("Import error:", err);
            toast.error("Failed to parse or upload Excel file.");
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-black mb-2">Developers Portal</h1>
                    <p className="text-black text-[14px] md:text-[15px] font-bold">Manage developer partnerships and their active property listings.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImportExcel}
                        accept=".xlsx, .xls"
                        className="hidden"
                    />
                    <button
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-md text-[13px] font-bold transition-all shadow-sm"
                        onClick={() => generateDeveloperSampleExcel()}
                    >
                        <i className="fa-solid fa-download"></i>
                        <span>Sample Template</span>
                    </button>
                    <button
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 rounded-md text-[13px] font-bold transition-all shadow-sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importing}
                    >
                        {importing ? (
                            <i className="fa-solid fa-spinner fa-spin"></i>
                        ) : (
                            <i className="fa-solid fa-file-import"></i>
                        )}
                        <span>{importing ? 'Importing...' : 'Import Excel'}</span>
                    </button>
                    <button
                        className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[#05144b] text-white hover:bg-[#0a1d63] rounded-md text-[14px] font-bold transition-all transform active:scale-95 group shadow-lg"
                        onClick={() => navigate('/admin-add-developer')}
                    >
                        <i className="fa-solid fa-plus transition-transform group-hover:rotate-90"></i>
                        <span>Add New Developer</span>
                    </button>
                </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
                <div className="relative w-full md:w-96">
                    <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                        type="text"
                        placeholder="Search developers by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#05144b]/10 focus:border-[#05144b] outline-none transition-all text-sm font-medium"
                    />
                </div>
                
                {selectedIds.length > 0 && (
                    <button
                        onClick={deleteSelected}
                        className="flex items-center gap-2 px-4 py-2.5 bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 rounded-lg text-sm font-bold transition-all animate-in fade-in zoom-in-95"
                    >
                        <i className="fa-solid fa-trash-can"></i>
                        <span>Delete Selected ({selectedIds.length})</span>
                    </button>
                )}
            </div>

            {msg && (
                <div className="mb-6 p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800 flex items-center gap-3 animate-in fade-in slide-in-from-top-4">
                    <i className="fa-solid fa-circle-check"></i>
                    <p className="text-sm font-medium">{msg}</p>
                </div>
            )}

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden">
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-20 text-center">
                            <div className="animate-spin w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full mx-auto mb-4"></div>
                            <p className="text-black font-bold text-[14px]">Synchronizing Developers...</p>
                        </div>
                    ) : developers.length === 0 ? (
                        <div className="p-20 text-center">
                            <div className="w-20 h-20 bg-white dark:bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-100 dark:text-slate-700 text-3xl">
                                <i className="fa-solid fa-building-circle-exclamation"></i>
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">No Developers Found</h3>
                            <p className="text-blue-950 dark:text-blue-950 text-sm mt-1">Start by adding your first development partner</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-white/50 dark:bg-slate-800/30 border-b border-blue-900/10 dark:border-slate-800">
                                    <th className="px-6 py-4 border-b border-gray-100">
                                        <input 
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-gray-300 text-[#05144b] focus:ring-[#05144b] cursor-pointer"
                                            checked={filteredDevelopers.length > 0 && selectedIds.length === filteredDevelopers.length}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                    <th className="px-6 py-4 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Developer</th>
                                    <th className="px-6 py-4 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Identity</th>
                                    <th className="px-6 py-4 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100 text-center">Featured</th>
                                    <th className="px-6 py-4 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Status</th>
                                    <th className="px-6 py-4 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100 text-center">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filteredDevelopers.map(d => (
                                    <tr key={d._id} className={`group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all ${selectedIds.includes(d._id) ? 'bg-blue-50/50' : ''}`}>
                                        <td className="px-6 py-4">
                                            <input 
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-gray-300 text-[#05144b] focus:ring-[#05144b] cursor-pointer"
                                                checked={selectedIds.includes(d._id)}
                                                onChange={() => toggleSelectOne(d._id)}
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="relative w-14 h-14 shrink-0 transition-transform group-hover:scale-110">
                                                {d.avatar ? (
                                                    <img src={getImageUrl(d.avatar)} alt={d.name} className="w-full h-full rounded-md object-cover border-2 border-white dark:border-slate-800 shadow-sm" />
                                                ) : (
                                                    <div className="w-full h-full rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-blue-500/20">
                                                        {d.name.charAt(0)}
                                                    </div>
                                                )}
                                                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-900 ${d.isActive ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-black dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors text-[14px] md:text-[15px]">{d.name}</div>
                                            <div className="text-[12px] text-black/60 mt-1 flex items-center gap-1.5 font-medium">
                                                <i className="fa-regular fa-envelope opacity-70"></i>
                                                {d.email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button 
                                                onClick={() => toggleFeatured(d)}
                                                className={`w-10 h-10 rounded-full inline-flex items-center justify-center transition-all ${d.isFeatured ? 'bg-amber-100 text-amber-500 shadow-inner' : 'bg-gray-100 text-gray-400 hover:text-amber-400'}`}
                                                title={d.isFeatured ? "Unfeature Developer" : "Feature Developer"}
                                            >
                                                <i className={`fa-solid fa-star ${d.isFeatured ? 'scale-110 text-lg' : 'scale-100'}`}></i>
                                            </button>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <button 
                                                    onClick={() => toggleStatus(d)}
                                                    className={`
                                                        relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-0
                                                        ${d.isActive ? 'bg-emerald-500' : 'bg-blue-100 dark:bg-slate-700'}
                                                    `}
                                                >
                                                    <span className={`
                                                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                                        ${d.isActive ? 'translate-x-5' : 'translate-x-0'}
                                                    `} />
                                                </button>
                                                <span className={`text-[12px] font-bold ${d.isActive ? 'text-emerald-500' : 'text-black'}`}>
                                                    {d.isActive ? 'Active' : 'Locked'}
                                                </span>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button 
                                                    onClick={() => navigate('/admin-edit-developer/' + d._id)}
                                                    className="w-10 h-10 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                    title="Edit Developer"
                                                >
                                                    <i className="fa-solid fa-pen-to-square text-xs"></i>
                                                </button>
                                                <button 
                                                    onClick={() => deleteDev(d._id)}
                                                    className="w-10 h-10 rounded-md bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                    title="Remove Developer"
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                confirmText="Delete Now"
                cancelText="Keep Developer"
                type="danger"
            />
        </AdminLayout>
    );
}


